import { useCallback, useEffect, useId, useRef, useState } from "react";
import type {
  AccountDialing,
  DialingApplyOperation,
  DialingApplyResult,
  ServiceConnections,
} from "@voice/api-contract";
import { checkDialing } from "@voice/domain";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type PendingApply = { requestKey: string; expectedVersion: number };
type Props = {
  api: Api;
  csrf?: string | undefined;
  accountId: string;
  customer: boolean;
  readOnly?: boolean;
  numbers: ServiceConnections["numbers"];
  initialDialing?: AccountDialing | undefined;
  refreshVersion: number;
  onChanged?: ((dialing: AccountDialing) => void) | undefined;
};
type View = {
  scope: string;
  data: AccountDialing | null;
  selected: string;
  confirmed: boolean;
  busy: boolean;
  error: string;
  notice: string;
  pending: PendingApply | null;
  operation: DialingApplyOperation | null;
  notRecorded: boolean;
};
const storageKey = (scope: string) => `crtc:account-dialing:v1:${scope}`;
function storedApply(scope: string): PendingApply | null {
  try {
    const raw = localStorage.getItem(storageKey(scope));
    if (!raw) return null;
    const value = JSON.parse(raw) as PendingApply;
    return typeof value.requestKey === "string" &&
      /^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}$/i.test(value.requestKey) &&
      Number.isSafeInteger(value.expectedVersion) &&
      value.expectedVersion >= 0
      ? value
      : null;
  } catch {
    return null;
  }
}
function rememberApply(scope: string, value: PendingApply) {
  localStorage.setItem(storageKey(scope), JSON.stringify(value));
}
function forgetApply(scope: string) {
  try {
    localStorage.removeItem(storageKey(scope));
  } catch {
    /* Reconciliation remains safe if the browser retains the key. */
  }
}
const blank = (scope: string, data: AccountDialing | null = null): View => ({
  scope,
  data,
  selected: data?.primaryNumberId ?? "",
  confirmed: false,
  busy: false,
  error: "",
  notice: "",
  pending: null,
  operation: null,
  notRecorded: false,
});
async function bounded<T>(
  api: Api,
  path: string,
  options: Parameters<Api>[1] = {},
): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      api<T>(path, { ...options, signal: controller.signal }),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(
            new Error(
              "The request timed out. Refresh the current status before trying again.",
            ),
          );
        }, 10_000);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export function dialingStatusLabel(data: AccountDialing): string {
  if (data.status === "applied" && data.appliedVersion === data.version)
    return "Applied to the phone system";
  if (data.status === "invalid_primary")
    return "Saved primary number is unavailable";
  if (data.status === "not_configured") return "No primary number selected";
  if (data.status === "unknown") return "Apply outcome needs confirmation";
  if (data.status === "unavailable") return "Phone-system status unavailable";
  return "Saved; not applied to the phone system";
}

export function AccountDialingSettings(props: Props) {
  const { api, accountId, customer, readOnly, csrf, refreshVersion } = props;
  const base = `/${customer ? "customer" : "admin"}/accounts/${accountId}/dialing`;
  const [state, setState] = useState<View>(() =>
    blank(base, props.initialDialing ?? null),
  );
  const view = state.scope === base ? state : blank(base);
  const currentScope = useRef(base);
  const allowed = useRef(false);
  const alive = useRef(true);
  const changed = useRef(props.onChanged);
  const inFlight = useRef<{ scope: string } | null>(null);
  const saveIntent = useRef<{ fingerprint: string; requestKey: string } | null>(
    null,
  );
  currentScope.current = base;
  allowed.current = !customer && !readOnly;
  changed.current = props.onChanged;
  const selectId = useId();
  const active = useCallback(
    (token: { scope: string }) =>
      alive.current &&
      currentScope.current === token.scope &&
      inFlight.current === token,
    [],
  );
  const update = useCallback(
    (token: { scope: string }, fn: (previous: View) => View) => {
      if (active(token))
        setState((previous) =>
          previous.scope === token.scope ? fn(previous) : previous,
        );
    },
    [active],
  );
  const accept = useCallback(
    (
      token: { scope: string },
      data: AccountDialing,
      extra: Partial<View> = {},
    ) => {
      update(token, (previous) => ({
        ...previous,
        data,
        confirmed: true,
        selected:
          previous.data?.version === data.version
            ? previous.selected
            : (data.primaryNumberId ?? ""),
        error: "",
        ...extra,
      }));
      if (active(token)) changed.current?.(data);
    },
    [active, update],
  );
  const acceptOperation = useCallback(
    (
      token: { scope: string },
      result: DialingApplyResult,
      pending: PendingApply,
    ) => {
      if (!active(token)) return;
      if (
        result.operation.requestKey !== pending.requestKey ||
        result.operation.version !== pending.expectedVersion
      )
        throw new Error(
          "The apply status did not match this request. Refresh the current status.",
        );
      const unresolved =
        result.operation.status === "applying" ||
        result.operation.status === "unknown" ||
        result.operation.safeToRetry;
      if (!unresolved) forgetApply(token.scope);
      accept(token, result.dialing, {
        pending: unresolved ? pending : null,
        operation: result.operation,
        notRecorded: false,
        notice:
          result.operation.status === "rejected" ? "" : result.operation.detail,
        error:
          result.operation.status === "rejected" ? result.operation.detail : "",
      });
    },
    [accept, active],
  );
  const read = useCallback(
    async (token: { scope: string }, pending: PendingApply | null) => {
      if (pending) {
        try {
          const result = await bounded<DialingApplyResult>(
            api,
            `${token.scope}?requestKey=${encodeURIComponent(pending.requestKey)}`,
          );
          acceptOperation(token, result, pending);
          return result;
        } catch (error) {
          if ((error as { status?: number } | null)?.status !== 404)
            throw error;
          const data = await bounded<AccountDialing>(api, token.scope);
          if (data.version !== pending.expectedVersion) {
            if (active(token)) forgetApply(token.scope);
            accept(token, data, {
              pending: null,
              operation: null,
              notRecorded: false,
              notice:
                "This request was not submitted and the saved setting changed. Review the current setting before applying.",
            });
            return {
              notRecorded: true as const,
              stale: true as const,
              dialing: data,
            };
          }
          accept(token, data, {
            pending,
            operation: null,
            notRecorded: true,
            notice:
              "This apply request has not been recorded. You may retry this same request after its saved version is checked again.",
          });
          return { notRecorded: true as const, dialing: data };
        }
      }
      const data = await bounded<AccountDialing>(api, token.scope);
      accept(token, data, {
        pending: null,
        operation: null,
        notRecorded: false,
      });
      return null;
    },
    [accept, acceptOperation, api],
  );
  const refresh = useCallback(async () => {
    if (inFlight.current?.scope === base) return;
    const token = { scope: base };
    inFlight.current = token;
    const pending = storedApply(base);
    setState((previous) => ({
      ...(previous.scope === base ? previous : blank(base)),
      busy: true,
      error: "",
      pending,
      notRecorded: false,
      operation: null,
    }));
    try {
      await read(token, pending);
    } catch (error) {
      update(token, (previous) => ({
        ...previous,
        confirmed: false,
        error: (error as Error).message,
      }));
    } finally {
      update(token, (previous) => ({ ...previous, busy: false }));
      if (inFlight.current === token) inFlight.current = null;
    }
  }, [base, read, update]);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      inFlight.current = null;
    };
  }, []);
  useEffect(() => {
    inFlight.current = null;
    saveIntent.current = null;
    setState(blank(base, props.initialDialing ?? null));
    void refresh();
    return () => {
      if (inFlight.current?.scope === base) inFlight.current = null;
    };
    // Dedicated reads establish status; a connection poll must not replace an open selection.
  }, [base, refreshVersion, refresh]);

  const save = async () => {
    if (
      !allowed.current ||
      !view.data ||
      !view.confirmed ||
      view.pending ||
      view.data.status === "unknown" ||
      inFlight.current?.scope === base
    )
      return;
    if (storedApply(base)) {
      void refresh();
      return;
    }
    const token = { scope: base };
    inFlight.current = token;
    update(token, (previous) => ({
      ...previous,
      busy: true,
      error: "",
      notice: "",
    }));
    const body = {
      expectedVersion: view.data.version,
      primaryNumberId: view.selected || null,
    };
    const fingerprint = JSON.stringify({ base, ...body });
    if (saveIntent.current?.fingerprint !== fingerprint)
      saveIntent.current = { fingerprint, requestKey: crypto.randomUUID() };
    try {
      const data = await bounded<AccountDialing>(api, base, {
        method: "POST",
        csrf,
        body: { ...body, requestKey: saveIntent.current.requestKey },
      });
      if (active(token)) saveIntent.current = null;
      accept(token, data, {
        selected: data.primaryNumberId ?? "",
        notice:
          "Primary number saved. Apply it to update seven-digit dialing on the phone system.",
      });
    } catch (error) {
      update(token, (previous) => ({
        ...previous,
        confirmed: false,
        error: `${(error as Error).message} Refresh dialing status before making another change.`,
      }));
    } finally {
      update(token, (previous) => ({ ...previous, busy: false }));
      if (inFlight.current === token) inFlight.current = null;
    }
  };
  const apply = async (retry = false) => {
    if (
      !allowed.current ||
      !view.data ||
      !view.confirmed ||
      inFlight.current?.scope === base
    )
      return;
    const token = { scope: base };
    inFlight.current = token;
    update(token, (previous) => ({
      ...previous,
      busy: true,
      error: "",
      notice: "",
    }));
    let pending = storedApply(base) ?? view.pending;
    try {
      if (pending) {
        const result = await read(token, pending);
        if (result && "stale" in result) {
          pending = null;
          return;
        }
        const retryAllowed =
          result && ("notRecorded" in result || result.operation.safeToRetry);
        if (!retry || !retryAllowed || !active(token) || !allowed.current)
          return;
        if (result.dialing.version !== pending.expectedVersion)
          throw new Error(
            "The saved settings changed while this request was checked.",
          );
      } else {
        if (
          retry ||
          view.selected !== (view.data.primaryNumberId ?? "") ||
          view.data.status === "invalid_primary" ||
          view.data.status === "unknown" ||
          view.data.version === 0
        )
          return;
        pending = {
          requestKey: crypto.randomUUID(),
          expectedVersion: view.data.version,
        };
        try {
          rememberApply(base, pending);
        } catch {
          pending = null;
          throw new Error(
            "This browser could not keep the apply request status. Refresh the page before applying.",
          );
        }
      }
      update(token, (previous) => ({
        ...previous,
        pending,
        notRecorded: false,
        operation: null,
      }));
      const result = await bounded<DialingApplyResult>(api, `${base}/apply`, {
        method: "POST",
        csrf,
        body: pending,
      });
      acceptOperation(token, result, pending);
    } catch (error) {
      update(token, (previous) => ({
        ...previous,
        pending,
        operation: null,
        notRecorded: false,
        error: `${(error as Error).message}${pending ? " Check this apply request's status before retrying." : ""}`,
        data:
          previous.data && pending
            ? { ...previous.data, status: "unknown" }
            : previous.data,
      }));
    } finally {
      update(token, (previous) => ({ ...previous, busy: false }));
      if (inFlight.current === token) inFlight.current = null;
    }
  };
  const eligible = props.numbers.filter(
    (number) =>
      number.eligible &&
      ["reserved", "active"].includes(number.status) &&
      checkDialing(number.number).destination === number.number,
  );
  const data = view.data;
  const dirty = !!data && view.selected !== (data.primaryNumberId ?? "");
  const writable = !customer && !readOnly;
  const applied =
    data?.status === "applied" && data.appliedVersion === data.version;
  const unresolved = !!view.pending || data?.status === "unknown";
  return (
    <section
      className="cr-panel cr-connection-section cr-dialing-settings"
      aria-label="Primary number and local dialing"
    >
      <h3>Primary number and seven-digit dialing</h3>
      <p>
        Seven-digit calls use the area code of this account's primary number.
        The seven digits you dial already include the exchange.
      </p>
      {!data && (
        <p role="status">
          {view.error
            ? "Dialing settings could not be loaded."
            : "Loading dialing settings…"}
        </p>
      )}
      {data && (
        <>
          <dl className="cr-details">
            <div>
              <dt>Saved primary number</dt>
              <dd>
                {data.primaryNumber ?? "Not selected or no longer available"}
              </dd>
            </div>
            <div>
              <dt>Seven-digit area code</dt>
              <dd>{data.localNpa ?? "Not configured"}</dd>
            </div>
            <div>
              <dt>Phone-system status</dt>
              <dd>
                {view.confirmed
                  ? dialingStatusLabel(data)
                  : view.error
                    ? "Current status is not confirmed"
                    : "Checking phone-system status…"}
              </dd>
            </div>
            {data.checkedAt && (
              <div>
                <dt>Last checked</dt>
                <dd>{new Date(data.checkedAt).toLocaleString()}</dd>
              </div>
            )}
          </dl>
          {data.status === "invalid_primary" && (
            <p className="cr-notice error">
              Choose another owned number. The previous primary is no longer
              available for this account.
            </p>
          )}
          {data.status === "unavailable" && (
            <p className="cr-notice">
              The saved primary is shown, but its phone-system setting could not
              be confirmed.
            </p>
          )}
          {writable ? (
            <form
              className="cr-form"
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <fieldset
                className="cr-connection-fields"
                disabled={view.busy || !view.confirmed || unresolved}
              >
                <legend>Choose this account's primary number</legend>
                <label className="cr-field" htmlFor={selectId}>
                  <span>Owned primary number</span>
                  <select
                    id={selectId}
                    value={view.selected}
                    onChange={(event) =>
                      setState((previous) => ({
                        ...previous,
                        selected: event.target.value,
                        notice: "",
                      }))
                    }
                  >
                    <option value="">No primary number</option>
                    {view.selected &&
                      !eligible.some(
                        (number) => number.id === view.selected,
                      ) && (
                        <option value={view.selected} disabled>
                          Previous primary number unavailable
                        </option>
                      )}
                    {eligible.map((number) => (
                      <option key={number.id} value={number.id}>
                        {number.number}
                      </option>
                    ))}
                  </select>
                </label>
              </fieldset>
              {!eligible.length && (
                <p className="cr-form-note">
                  Add an owned, available North American number to this account
                  in Numbers before selecting a primary.
                </p>
              )}
              <div className="cr-actions">
                <button
                  className="cr-button secondary"
                  disabled={
                    view.busy || !view.confirmed || unresolved || !dirty
                  }
                >
                  Save primary number
                </button>
                <button
                  type="button"
                  className="cr-button"
                  disabled={
                    view.busy ||
                    !view.confirmed ||
                    unresolved ||
                    dirty ||
                    applied ||
                    data.status === "invalid_primary" ||
                    data.version === 0
                  }
                  onClick={() => {
                    void apply();
                  }}
                >
                  Apply dialing to phone system
                </button>
              </div>
              <p className="cr-form-note">
                Saving updates the portal record. Apply updates the phone
                system; confirm the status above before using seven-digit calls.
                Clearing the primary disables seven-digit dialing after it is
                applied.
              </p>
            </form>
          ) : (
            <p className="cr-form-note">
              Cache River staff manage the primary number and apply dialing
              changes.
            </p>
          )}
        </>
      )}
      {view.notice && (
        <p className="cr-notice" role="status">
          {view.notice}
        </p>
      )}
      {view.error && (
        <p className="cr-notice error" role="alert">
          {view.error}
        </p>
      )}
      {view.pending && (
        <div className="cr-notice" role="status">
          <p>
            This apply request needs confirmation. Check its status before
            starting another apply.
          </p>
          {writable && (view.notRecorded || view.operation?.safeToRetry) && (
            <button
              className="cr-button secondary"
              disabled={view.busy}
              onClick={() => {
                void apply(true);
              }}
            >
              Retry the same apply request
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        className="cr-text-button"
        disabled={view.busy}
        onClick={() => {
          void refresh();
        }}
      >
        {view.pending ? "Check apply request status" : "Refresh dialing status"}
      </button>
    </section>
  );
}
